// client/src/views/Sidebar_parts/PathsAndZonesSection.js
import React, { useState, useEffect } from "react";

function isZoneVisibleToEndUser(zone) {
  const state = zone.behaviorState || zone.behavior_state || {};
  if (
    state &&
    typeof state === "object" &&
    Object.prototype.hasOwnProperty.call(state, "visible_to_users")
  ) {
    return state.visible_to_users !== false;
  }
  return true;
}

function isZoneHideTickEnabled(zone) {
  const state = zone.behaviorState || zone.behavior_state || {};
  if (
    state &&
    typeof state === "object" &&
    Object.prototype.hasOwnProperty.call(state, "hide_tick")
  ) {
    return !!state.hide_tick;
  }
  return false;
}

function getDestinationMapId(zone) {
  const props = zone.behaviorProps || zone.behavior_props || {};
  if (!props || typeof props !== "object") return null;
  const raw =
    props.destinationMapId !== undefined
      ? props.destinationMapId
      : props.destination_map_id;
  if (raw === null || raw === undefined) return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function isZoneClickable(zone) {
  const state = zone.behaviorState || zone.behavior_state || {};
  if (
    state &&
    typeof state === "object" &&
    Object.prototype.hasOwnProperty.call(state, "clickable")
  ) {
    return !!state.clickable;
  }
  const destId = getDestinationMapId(zone);
  return !!destId;
}

function sortZonesByLabel(zones, isPath = false) {
  const fallback = isPath ? "Untitled Path" : "Untitled";
  return [...zones].sort((a, b) => {
    const rawA = a.label && a.label.trim() ? a.label.trim() : fallback;
    const rawB = b.label && b.label.trim() ? b.label.trim() : fallback;
    return rawA.localeCompare(rawB, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

function PathsAndZonesSection({
  zones = [],
  visibleZones,
  setVisibleZones,
  pathControls = {},
  onPathControlChange,
  mapId,
  sectionNamePaths,
  sectionNameZones,
  isMapOwner,
  onToggleZoneUserVisibility,
  setHoveredZoneId,
  setHideSidebarHover,
  hasSearch,
  normalizedSearch,
  onZoneTeleport,
  activeOverlayId = null,
  hasActiveOverlay = false,
  rememberMapPrefs = true,
}) {
  const [zonesExpanded, setZonesExpanded] = useState(false);
  const [pathsExpanded, setPathsExpanded] = useState(false);
  const [zoneUserVisibility, setZoneUserVisibility] = useState({});
  const [zoneHideTick, setZoneHideTick] = useState({});
  const [zoneClickable, setZoneClickable] = useState({});

  const effectiveHasActiveOverlay =
    activeOverlayId != null ? true : !!hasActiveOverlay;

  const overlayFilteredZones = Array.isArray(zones)
    ? zones.filter((zone) => {
        let overlayId =
          zone.overlay_id != null
            ? zone.overlay_id
            : zone.overlayId != null
            ? zone.overlayId
            : null;

        if (overlayId === "" || overlayId === undefined) {
          overlayId = null;
        }

        if (overlayId === null) return true;

        const numericOverlayId = Number(overlayId);
        if (!Number.isFinite(numericOverlayId)) {
          return true;
        }

        if (numericOverlayId === 1) {
          return !effectiveHasActiveOverlay;
        }

        if (!effectiveHasActiveOverlay) return false;

        const activeIdNum = Number(activeOverlayId);
        if (!Number.isFinite(activeIdNum)) return false;

        return String(activeIdNum) === String(numericOverlayId);
      })
    : [];

  const pathZones = sortZonesByLabel(
    overlayFilteredZones.filter(
      (z) =>
        (z.behavior_type || z.behaviorType || z.behavior) === "pathing"
    ),
    true
  );

  const normalZones = sortZonesByLabel(
    overlayFilteredZones.filter(
      (z) =>
        (z.behavior_type || z.behaviorType || z.behavior) !== "pathing"
    ),
    false
  );

  const displayZones = isMapOwner
    ? normalZones
    : normalZones.filter(isZoneVisibleToEndUser);

  const anyZoneTickVisibleToEndUser =
    !isMapOwner &&
    displayZones.some((zone) => {
      const initialHideTick = isZoneHideTickEnabled(zone);
      const effectiveHideTick =
        Object.prototype.hasOwnProperty.call(zoneHideTick, zone.id)
          ? zoneHideTick[zone.id]
          : initialHideTick;
      return !effectiveHideTick;
    });

  const allZonesVisible =
    displayZones.length > 0 &&
    displayZones.every((z) => visibleZones && visibleZones.has(z.id));

  const allPathsVisible =
    pathZones.length > 0 &&
    pathZones.every((z) => visibleZones && visibleZones.has(z.id));

  const safePathControls = pathControls || {};
  const pathStateKey = mapId ? `pathSection_${mapId}` : null;
  const zoneStateKey = mapId ? `zoneSection_${mapId}` : null;

  const savePathState = (expandedValue, visibleZonesSnapshot) => {
    if (!rememberMapPrefs) return;
    if (!pathStateKey || typeof window === "undefined") return;
    try {
      const visiblePathIds = pathZones
        .filter((z) => visibleZonesSnapshot.has(z.id))
        .map((z) => z.id);
      const data = {
        expanded: !!expandedValue,
        visiblePathIds,
      };
      window.localStorage.setItem(pathStateKey, JSON.stringify(data));
    } catch {}
  };

  useEffect(() => {
    if (!rememberMapPrefs) return;
    if (!pathStateKey || typeof window === "undefined") return;
    if (!zones || zones.length === 0) return;

    try {
      const raw = window.localStorage.getItem(pathStateKey);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || typeof data !== "object") return;

      if (typeof data.expanded === "boolean") {
        setPathsExpanded(data.expanded);
      }
      if (Array.isArray(data.visiblePathIds)) {
        const visibleSet = new Set(data.visiblePathIds);
        setVisibleZones((prev) => {
          const next = new Set(prev || []);
          for (const z of pathZones) {
            if (visibleSet.has(z.id)) next.add(z.id);
            else next.delete(z.id);
          }
          return next;
        });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathStateKey, zones.length, rememberMapPrefs]);

  useEffect(() => {
    if (!rememberMapPrefs) return;
    if (!zoneStateKey || typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(zoneStateKey);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data && typeof data.expanded === "boolean") {
        setZonesExpanded(data.expanded);
      }
    } catch {}
  }, [zoneStateKey, rememberMapPrefs]);

  const toggleZoneVisibility = (zoneId) => {
    setVisibleZones((prev) => {
      const next = new Set(prev || []);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return new Set(next);
    });
  };

  const selectAllZones = () => {
    setVisibleZones(new Set(displayZones.map((z) => z.id)));
  };

  const deselectAllZones = () => {
    setVisibleZones(new Set());
  };

  const handleTogglePathsExpanded = () => {
    setPathsExpanded((prev) => {
      const next = !prev;
      savePathState(next, visibleZones || new Set());
      return next;
    });
  };

  const togglePathVisibility = (zoneId) => {
    setVisibleZones((prev) => {
      const next = new Set(prev || []);
      const willBeVisible = !next.has(zoneId);
      if (willBeVisible) {
        next.add(zoneId);
      } else {
        next.delete(zoneId);
      }
      savePathState(pathsExpanded, next);
      if (onPathControlChange) {
        onPathControlChange(zoneId, { hidden: !willBeVisible });
      }
      return next;
    });
  };

  const handleToggleAllPaths = () => {
    const newAllVisible = !allPathsVisible;

    setVisibleZones((prev) => {
      const next = new Set(prev || []);
      if (newAllVisible) {
        pathZones.forEach((z) => next.add(z.id));
      } else {
        pathZones.forEach((z) => next.delete(z.id));
      }
      savePathState(pathsExpanded, next);
      return next;
    });

    if (onPathControlChange) {
      onPathControlChange("__all", { hidden: !newAllVisible });
    }
  };

  const handleToggleZonesExpanded = () => {
    setZonesExpanded((prev) => {
      const next = !prev;
      if (rememberMapPrefs && zoneStateKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            zoneStateKey,
            JSON.stringify({ expanded: next })
          );
        } catch {}
      }
      return next;
    });
  };

  const handleZoneUserVisibilityToggle = (zone) => {
    const zoneId = zone.id;
    const baseState =
      zone.behaviorState || zone.behavior_state || {};
    const initialVisible = isZoneVisibleToEndUser(zone);
    const initialHideTick = isZoneHideTickEnabled(zone);
    const initialClickable = isZoneClickable(zone);

    setZoneUserVisibility((prev) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(
        prev,
        zoneId
      );
      const currentVisible = hasOverride ? prev[zoneId] : initialVisible;
      const nextVisible = !currentVisible;

      const effectiveHideTick =
        Object.prototype.hasOwnProperty.call(zoneHideTick, zoneId)
          ? zoneHideTick[zoneId]
          : initialHideTick;

      const effectiveClickable =
        Object.prototype.hasOwnProperty.call(zoneClickable, zoneId)
          ? zoneClickable[zoneId]
          : initialClickable;

      const nextState = {
        ...baseState,
        visible_to_users: nextVisible,
        hide_tick: effectiveHideTick,
        clickable: effectiveClickable,
      };

      if (onToggleZoneUserVisibility) {
        onToggleZoneUserVisibility(zoneId, nextVisible, nextState);
      }

      if (typeof fetch !== "undefined" && zoneId != null) {
        try {
          fetch(`/api/polygons/${zoneId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ behaviorState: nextState }),
          }).catch(() => {});
        } catch {}
      }

      return {
        ...prev,
        [zoneId]: nextVisible,
      };
    });
  };

  const handleZoneHideTickToggle = (zone) => {
    const zoneId = zone.id;
    const baseState =
      zone.behaviorState || zone.behavior_state || {};
    const initialVisible = isZoneVisibleToEndUser(zone);
    const initialHideTick = isZoneHideTickEnabled(zone);
    const initialClickable = isZoneClickable(zone);

    setZoneHideTick((prev) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(
        prev,
        zoneId
      );
      const currentHideTick = hasOverride ? prev[zoneId] : initialHideTick;
      const nextHideTick = !currentHideTick;

      const effectiveVisible =
        Object.prototype.hasOwnProperty.call(
          zoneUserVisibility,
          zoneId
        )
          ? zoneUserVisibility[zoneId]
          : initialVisible;

      const effectiveClickable =
        Object.prototype.hasOwnProperty.call(zoneClickable, zoneId)
          ? zoneClickable[zoneId]
          : initialClickable;

      const nextState = {
        ...baseState,
        visible_to_users: effectiveVisible,
        hide_tick: nextHideTick,
        clickable: effectiveClickable,
      };

      if (onToggleZoneUserVisibility) {
        onToggleZoneUserVisibility(
          zoneId,
          effectiveVisible,
          nextState
        );
      }

      if (typeof fetch !== "undefined" && zoneId != null) {
        try {
          fetch(`/api/polygons/${zoneId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ behaviorState: nextState }),
          }).catch(() => {});
        } catch {}
      }

      return {
        ...prev,
        [zoneId]: nextHideTick,
      };
    });
  };

  const handleZoneClickableToggle = (zone) => {
    const zoneId = zone.id;
    const baseState =
      zone.behaviorState || zone.behavior_state || {};
    const initialVisible = isZoneVisibleToEndUser(zone);
    const initialHideTick = isZoneHideTickEnabled(zone);
    const initialClickable = isZoneClickable(zone);

    setZoneClickable((prev) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(
        prev,
        zoneId
      );
      const currentClickable = hasOverride
        ? prev[zoneId]
        : initialClickable;
      const nextClickable = !currentClickable;

      const effectiveVisible =
        Object.prototype.hasOwnProperty.call(
          zoneUserVisibility,
          zoneId
        )
          ? zoneUserVisibility[zoneId]
          : initialVisible;

      const effectiveHideTick =
        Object.prototype.hasOwnProperty.call(zoneHideTick, zoneId)
          ? zoneHideTick[zoneId]
          : initialHideTick;

      const nextState = {
        ...baseState,
        visible_to_users: effectiveVisible,
        hide_tick: effectiveHideTick,
        clickable: nextClickable,
      };

      if (onToggleZoneUserVisibility) {
        onToggleZoneUserVisibility(
          zoneId,
          effectiveVisible,
          nextState
        );
      }

      if (typeof fetch !== "undefined" && zoneId != null) {
        try {
          fetch(`/api/polygons/${zoneId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ behaviorState: nextState }),
          }).catch(() => {});
        } catch {}
      }

      return {
        ...prev,
        [zoneId]: nextClickable,
      };
    });
  };

  const handleTeleportClick = (zone) => {
    const destMapId = getDestinationMapId(zone);
    if (!destMapId) return;

    if (onZoneTeleport) {
      onZoneTeleport(destMapId, zone);
      return;
    }

    if (typeof window === "undefined") return;

    window.location.href = `/map/${destMapId}`;
  };

  return (
    <>
      {pathZones.length > 0 && (
        <div
          style={{
            marginTop: 30,
            paddingLeft: 8,
            borderTop: "1px solid #444",
            userSelect: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
              marginTop: 16,
            }}
          >
            <div
              style={{
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "1.3em",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onClick={handleTogglePathsExpanded}
              aria-expanded={pathsExpanded}
              role="button"
              tabIndex={0}
            >
              {pathsExpanded ? "▼" : "►"} {sectionNamePaths}
            </div>
          </div>
          <div style={{ margin: "8px 0 4px 0", display: "flex", gap: 8 }}>
            <button
              onClick={handleToggleAllPaths}
              style={{
                fontSize: 14,
                padding: "2px 13px",
                borderRadius: 4,
                background: "#282931",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                marginRight: 4,
              }}
            >
              {allPathsVisible ? "Hide All" : "Show All"}
            </button>
            <button
              onClick={handleTogglePathsExpanded}
              title={pathsExpanded ? "Collapse Paths" : "Expand Paths"}
              style={{
                fontSize: 18,
                lineHeight: 1,
                padding: "2px 8px",
                borderRadius: 4,
                background: "#282931",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              {pathsExpanded ? "−" : "+"}
            </button>
          </div>
          {pathsExpanded && (
            <ul
              style={{ paddingLeft: 0, marginTop: 4, listStyle: "none" }}
            >
              {pathZones.map((zone) => {
                const label = zone.label?.trim() || "Untitled Path";
                const matchesSearch =
                  !hasSearch ||
                  label.toLowerCase().includes(normalizedSearch);
                if (!matchesSearch) return null;

                const ctrl = safePathControls[zone.id] || {};
                const isPaused = !!ctrl.paused;
                const speed =
                  typeof ctrl.speed === "number" ? ctrl.speed : 0.25;
                const position =
                  typeof ctrl.position === "number"
                    ? Math.min(Math.max(ctrl.position, 0), 1)
                    : 0;
                const pathVisible =
                  visibleZones && visibleZones.has(zone.id);

                return (
                  <li
                    key={zone.id}
                    style={{
                      marginBottom: 10,
                      padding: "6px 6px 10px 0",
                      borderRadius: 6,
                      background: "#23252d",
                    }}
                    onMouseEnter={() => {
                      setHoveredZoneId && setHoveredZoneId(zone.id);
                      setHideSidebarHover &&
                        setHideSidebarHover(true);
                    }}
                    onMouseLeave={() => {
                      setHoveredZoneId && setHoveredZoneId(null);
                      setHideSidebarHover &&
                        setHideSidebarHover(false);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginLeft: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!pathVisible}
                          onChange={() =>
                            togglePathVisibility(zone.id)
                          }
                          style={{ marginRight: 2 }}
                        />
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                          title={label}
                        >
                          {label}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          onPathControlChange &&
                          onPathControlChange(zone.id, {
                            paused: !isPaused,
                          })
                        }
                        style={{
                          fontSize: 12,
                          padding: "3px 10px",
                          borderRadius: 999,
                          border: "none",
                          cursor: "pointer",
                          background: isPaused
                            ? "#3a7cff"
                            : "#ffb347",
                          color: "#111",
                          fontWeight: 700,
                          marginRight: 6,
                        }}
                      >
                        {isPaused ? "Resume" : "Pause"}
                      </button>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginLeft: 4,
                        marginRight: 6,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          opacity: 0.8,
                          minWidth: 42,
                        }}
                      >
                        Speed
                      </span>
                      <input
                        type="range"
                        min={0.05}
                        max={1}
                        step={0.05}
                        value={speed}
                        onChange={(e) => {
                          const val =
                            parseFloat(e.target.value) || 0.05;
                          onPathControlChange &&
                            onPathControlChange(zone.id, {
                              speed: val,
                            });
                        }}
                        style={{ flex: 1 }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          width: 34,
                          textAlign: "right",
                          opacity: 0.85,
                        }}
                      >
                        {speed.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginLeft: 4,
                        marginRight: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          opacity: 0.8,
                          minWidth: 50,
                        }}
                      >
                        Position
                      </span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(position * 100)}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value, 10);
                          const pct = Number.isNaN(raw)
                            ? 0
                            : Math.min(Math.max(raw, 0), 100);
                          const normalized = pct / 100;
                          onPathControlChange &&
                            onPathControlChange(zone.id, {
                              position: normalized,
                            });
                        }}
                        style={{ flex: 1 }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          width: 34,
                          textAlign: "right",
                          opacity: 0.85,
                        }}
                      >
                        {Math.round(position * 100)}%
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {displayZones.length > 0 && (
        <div
          style={{
            marginTop: 30,
            paddingLeft: 8,
            borderTop: "1px solid #444",
            userSelect: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 0,
              marginTop: 16,
            }}
          >
            <div
              style={{
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "1.3em",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onClick={handleToggleZonesExpanded}
              aria-expanded={zonesExpanded}
              role="button"
              tabIndex={0}
            >
              {zonesExpanded ? "▼" : "►"} {sectionNameZones}
            </div>
          </div>
          <div style={{ margin: "8px 0 4px 0", display: "flex", gap: 8 }}>
            {(isMapOwner || anyZoneTickVisibleToEndUser) && (
              <button
                onClick={allZonesVisible ? deselectAllZones : selectAllZones}
                style={{
                  fontSize: 14,
                  padding: "2px 13px",
                  borderRadius: 4,
                  background: "#282931",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  marginRight: 4,
                }}
              >
                {allZonesVisible ? "Hide All" : "Show All"}
              </button>
            )}
            <button
              onClick={handleToggleZonesExpanded}
              title={zonesExpanded ? "Collapse Zones" : "Expand Zones"}
              style={{
                fontSize: 18,
                lineHeight: 1,
                padding: "2px 8px",
                borderRadius: 4,
                background: "#282931",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              {zonesExpanded ? "−" : "+"}
            </button>
          </div>
          {zonesExpanded && (
            <ul style={{ paddingLeft: 12, marginTop: 0 }}>
              {displayZones.map((zone) => {
                const label = zone.label?.trim() || "Untitled";
                const checked =
                  visibleZones && visibleZones.has(zone.id);
                const matchesSearch =
                  !hasSearch ||
                  label.toLowerCase().includes(normalizedSearch);
                if (!matchesSearch) return null;

                const initialVisibleToUsers =
                  isZoneVisibleToEndUser(zone);
                const effectiveVisibleToUsers =
                  Object.prototype.hasOwnProperty.call(
                    zoneUserVisibility,
                    zone.id
                  )
                    ? zoneUserVisibility[zone.id]
                    : initialVisibleToUsers;

                const initialHideTick = isZoneHideTickEnabled(zone);
                const effectiveHideTick =
                  Object.prototype.hasOwnProperty.call(
                    zoneHideTick,
                    zone.id
                  )
                    ? zoneHideTick[zone.id]
                    : initialHideTick;

                const initialClickable = isZoneClickable(zone);
                const effectiveClickable =
                  Object.prototype.hasOwnProperty.call(
                    zoneClickable,
                    zone.id
                  )
                    ? zoneClickable[zone.id]
                    : initialClickable;

                const destMapId = getDestinationMapId(zone);
                const canTeleport =
                  !!destMapId && !!effectiveClickable;

                const checkboxId = `zone-vis-${zone.id}`;

                return (
                  <li
                    key={zone.id}
                    style={{
                      marginBottom: 8,
                      paddingBottom: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                    onMouseEnter={() => {
                      setHoveredZoneId && setHoveredZoneId(zone.id);
                      setHideSidebarHover &&
                        setHideSidebarHover(true);
                    }}
                    onMouseLeave={() => {
                      setHoveredZoneId && setHoveredZoneId(null);
                      setHideSidebarHover &&
                        setHideSidebarHover(false);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {(!effectiveHideTick || isMapOwner) && (
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={() =>
                            toggleZoneVisibility(zone.id)
                          }
                          style={{ marginRight: 8 }}
                          id={checkboxId}
                        />
                      )}
                      {(!effectiveHideTick || isMapOwner) ? (
                        <label
                          htmlFor={checkboxId}
                          style={{
                            cursor: "pointer",
                            flexGrow: 1,
                            userSelect: "none",
                          }}
                        >
                          {label}
                        </label>
                      ) : (
                        <span
                          style={{
                            flexGrow: 1,
                            userSelect: "none",
                          }}
                        >
                          {label}
                        </span>
                      )}
                      {!isMapOwner && canTeleport && (
                        <button
                          type="button"
                          onClick={() => handleTeleportClick(zone)}
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 4,
                            border: "none",
                            cursor: "pointer",
                            background: "#3a7cff",
                            color: "#fff",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Open
                        </button>
                      )}
                    </div>

                    {isMapOwner && (
                      <div
                        style={{
                          marginLeft: 22,
                          marginTop: 2,
                          fontSize: 11,
                          opacity: 0.9,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          width: "calc(100% - 22px)",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!effectiveVisibleToUsers}
                            onChange={() =>
                              handleZoneUserVisibilityToggle(zone)
                            }
                          />
                          <span>Visible to users</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!effectiveHideTick}
                            onChange={() =>
                              handleZoneHideTickToggle(zone)
                            }
                          />
                          <span>Hide tick</span>
                        </label>
                        {destMapId && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginTop: 2,
                            }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!effectiveClickable}
                                onChange={() =>
                                  handleZoneClickableToggle(zone)
                                }
                              />
                              <span>Clickable</span>
                            </label>
                            {canTeleport && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleTeleportClick(zone)
                                }
                                style={{
                                  fontSize: 11,
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  border: "none",
                                  cursor: "pointer",
                                  background: "#3a7cff",
                                  color: "#fff",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Open
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

export default PathsAndZonesSection;
